import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiClient } from "@/config/api";
import { USStates } from "@/utils/countryStates";
import { PropertyType } from "@/types/enums";
import { DEFAULT_BEDROOM_COUNT, USER_TYPES } from "@/utils/constants";
import { handleApiError } from "@/utils/functions";
import {
  processAdditionalVendors,
  processBathrooms,
  processBedrooms,
  processSmartLock,
  processUnits,
  toAbsoluteUrl,
} from "@/utils/propertyConverters";
import { stripSignDocumentsForAdmin } from "@/utils/propertyCreationSteps";
import { RootState } from "@/types/store";

import {
  Amenity,
  CompleteFeaturesBody,
  CreateProperty,
  ConfirmPropertyImagesBody,
  CreatePropertyListing,
  CreatePropertyListingResponse,
  GetImagesPresignedUrlBody,
  ImagesPresignedUrlResponse,
  GetMapPinsBody,
  GetMorePropertiesBody,
  GetMorePropertiesForUserBody,
  GetPropertiesBody,
  PropertyCurrentStepResponse,
  SearchPropertiesBody,
  UpdatePropertyBody,
} from "./types";

export const getProperties = createAsyncThunk(
  "my-properties",
  async (
    { userType, statuses, cityStates, numberOfBedrooms, ...restOfData }: GetPropertiesBody,
    { rejectWithValue }
  ) => {
    try {
      const params = {
        ...restOfData,
        statuses,
        cityStates,
        numberOfBedrooms: numberOfBedrooms !== DEFAULT_BEDROOM_COUNT ? numberOfBedrooms : undefined,
      };
      if (userType === USER_TYPES.HOMEOWNER) {
        const response = await apiClient.get("my-properties", {
          params,
        });
        return response.data.data;
      } else {
        const response = await apiClient.get("properties", {
          params,
        });
        return response.data.data;
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get properties failed"
        );
      }
      return rejectWithValue("Get properties failed");
    }
  }
);

export const getProperty = createAsyncThunk(
  "property/:id",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`property/${id}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get property failed"
        );
      }
      return rejectWithValue("Get property failed");
    }
  }
);

export const getMoreProperties = createAsyncThunk(
  "properties/more",
  async (
    { currentPage, propertyIds, homeownerIds, userType, statuses, cityStates, numberOfBedrooms }: GetMorePropertiesBody,
    { rejectWithValue }
  ) => {
    try {
      const baseParams = {
        page: currentPage + 1,
        propertyIds,
        statuses,
        cityStates,
        numberOfBedrooms: numberOfBedrooms !== DEFAULT_BEDROOM_COUNT ? numberOfBedrooms : undefined,
      };
      if (userType === USER_TYPES.HOMEOWNER) {
        const response = await apiClient.get(`my-properties`, {
          params: baseParams,
        });
        return response.data.data;
      } else {
        const response = await apiClient.get(`properties`, {
          params: {
            ...baseParams,
            homeownerIds,
          },
        });
        return response.data.data;
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get properties failed"
        );
      }
      return rejectWithValue("Get properties failed");
    }
  }
);

// NOTE: Remove, duplicate of getProperty
export const getPropertyById = createAsyncThunk(
  "property/{id}",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`property/${id}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get property failed"
        );
      }
      return rejectWithValue("Get property failed");
    }
  }
);

// Super-Admin toggle for whether a Non-Renting property appears on the Tape
// Chart. Backend 400s (SHOW_ON_TAPE_CHART_NOT_APPLICABLE) if status !== NON_RENTING.
export const setShowOnTapeChart = createAsyncThunk(
  "property/show-on-tape-chart",
  async (
    {
      propertyId,
      showOnTapeChart,
    }: { propertyId: string; showOnTapeChart: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.patch(
        `property/${propertyId}/show-on-tape-chart`,
        { showOnTapeChart }
      );
      return response.data.data as { id: string; showOnTapeChart: boolean };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Failed to update Show on Tape Chart"
        );
      }
      return rejectWithValue("Failed to update Show on Tape Chart");
    }
  }
);

export const createPropertyType = createAsyncThunk(
  "create/property/type",
  async ({ type, hoId, fullCreation }: CreateProperty, { rejectWithValue }) => {
    const apiUrl = hoId ? `homeowner/${hoId}/properties` : "properties";
    try {
      const response = await apiClient.post(apiUrl, {
        type,
        // Only sent when overriding, so standard creation stays unchanged.
        ...(fullCreation ? { fullCreation: true } : {}),
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Create property type failed"
        );
      }
      return rejectWithValue("Create property type failed");
    }
  }
);

export const createPropertyListing = createAsyncThunk<
  CreatePropertyListingResponse,
  CreatePropertyListing,
  { rejectValue: string }
>(
  "create/property/listing",
  async ({ values, hoId }, { rejectWithValue }) => {
    try {
      const isMultiFamily = values.propertyType === PropertyType.DUPLEX;
      const bathrooms = isMultiFamily ? [] : processBathrooms(values);
      const bedrooms = isMultiFamily ? [] : processBedrooms(values);
      const additionalVendors = processAdditionalVendors(values);
      const units = processUnits(values.propertyUnits || []);

      const { smartLock, lockPin } = processSmartLock(values);

      const body = {
        name: values.propertyName,
        state: USStates[values.state as keyof typeof USStates],
        numberOfBedrooms: values.bedrooms,
        numberOfHalfBathrooms: values.halfBathrooms,
        numberOfFullBathrooms: values.fullBathrooms,
        numberOfParkingSpots: values.parkingSpaces,
        sleepTotal: values.totalBeds,
        size: values.size,
        streamlineAmenities: values.amenities.amenities.flatMap((a: Amenity) =>
          a.selected ? [a.id] : []
        ),
        propertyLink1: toAbsoluteUrl(values.propertyLink1),
        propertyLink2: toAbsoluteUrl(values.propertyLink2),
        wifiName: values.wifiName || null,
        wifiPassword: values.wifiPassword || null,
        description: values.description || null,
        streetAddress: values.streetAddress,
        city: values.city,
        zipCode: values.zipCode,
        country: values.country,
        smartLock,
        lockPin,
        guestCleaningService: values.guestCleaning,
        damageWaiverService: values.damageWaiver,
        petFriendly: values.isPetFriendly,
        maxPets: values.maxPets,
        bathrooms,
        bedrooms,
        pestControlCompanyName: values.pestControlCompany || null,
        pestControlCompanyPhoneNumber: values.pestControlPhone || null,
        poolCompanyName: values.poolCompany || null,
        poolCompanyPhoneNumber: values.poolPhone || null,
        poolServiceDay: [values.poolService].flat().filter((s) => s) || null,
        hvac: values.hvacCompany || null,
        hvacPhoneNumber: values.hvacPhone || null,
        hvacTrashPickupDay:
          [values.hvacService].flat().filter((s) => s) || null,
        houseManagerName: values.houseManagerName || null,
        apartment: values.apartmentUnit,
        houseManagerPhoneNumber: values.houseManagerPhone || null,
        landscapingCompanyName: values.landscapingCompany || null,
        landscapingCompanyPhoneNumber: values.landscapingPhone || null,
        landscapingServiceDay:
          [values.landscapingService].flat().filter((s) => s) || null,
        additionalVendors,
        units,
      };

      const apiUrl = `${hoId ? `/homeowner/${hoId}/` : ""}property/${
        values.id
      }/listing`;
      const response = await apiClient.post(apiUrl, body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Create property listing failed"
        );
      }
      return rejectWithValue("Create property listing failed");
    }
  }
);

export const getPropertiesForUser = createAsyncThunk(
  "properties/user",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`properties/user/${userId}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get properties failed"
        );
      }
      return rejectWithValue("Get properties failed");
    }
  }
);

export const getMorePropertiesForUser = createAsyncThunk(
  "properties/user/more",
  async (
    { userId, currentPage }: GetMorePropertiesForUserBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(`properties/user/${userId}`, {
        params: {
          page: currentPage + 1,
        },
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get properties failed"
        );
      }
      return rejectWithValue("Get properties failed");
    }
  }
);

export const getUnfinishedProperties = createAsyncThunk(
  "properties/unfinished-onboarding",
  async (
    { currentPage = 0 }: { currentPage?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `properties/unfinished-onboarding?page=${currentPage + 1}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get unfinished properties failed"
        );
      }
      return rejectWithValue("Get unfinished properties failed");
    }
  }
);

export const propertyOnboardingBack = createAsyncThunk(
  "/property/:id/onboarding-back",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/property/${id}/onboarding-back`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Go back to previous step failed"
        );
      }
      return rejectWithValue("Go back to previous step failed");
    }
  }
);

// Persists the uploaded photo keys, advances onboarding, AND re-syncs the
// gallery to Streamline — in one call. The keys MUST be passed here: advancing
// without them syncs the gallery empty and loses the photos. Omit addedImages
// (or send []) to skip the step with no photos.
export const propertyOnboardingPhotosNextStep = createAsyncThunk(
  "/property/:id/photos-next-step",
  async (
    { id, addedImages }: { id: string; addedImages?: { key: string }[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(
        `/property/${id}/photos-next-step`,
        { addedImages: addedImages ?? [] }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Property Onboarding next step failed"
        );
      }
      return rejectWithValue("Property Onboarding next step failed");
    }
  }
);

// `acknowledged` is the homeowner's self-attestation that they signed every
// required document. The BE requires it when a required document isn't marked
// signed on our side (documents awaiting an org countersignature never are),
// and ignores it otherwise.
export const propertyOnboardingDocumentsNextStep = createAsyncThunk(
  "/property/:id/docusign-confirm",
  async (
    { id, acknowledged = false }: { id: string; acknowledged?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(`/property/${id}/docusign-confirm`, {
        acknowledged,
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Property Onboarding documents next step failed"
        );
      }
      return rejectWithValue("Property Onboarding documents next step failed");
    }
  }
);


export const uploadDocumentsConfirm = createAsyncThunk(
  "/property/:id/upload-documents-confirm",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/property/${id}/upload-documents-confirm`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Upload documents confirm failed"
        );
      }
      return rejectWithValue("Upload documents confirm failed");
    }
  }
);

export const getImagesPresignedUrl = createAsyncThunk(
  "/property/:id/images-presigned-url",
  async (body: GetImagesPresignedUrlBody, { rejectWithValue }) => {
    try {
      const { id, images } = body;
      const response = await apiClient.post(
        `/property/${id}/images-presigned-url`,
        {
          images,
        }
      );
      return response.data.data as ImagesPresignedUrlResponse;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get images presigned url failed"
        );
      }
      return rejectWithValue("Get images presigned url failed");
    }
  }
);

// Persist gallery rows for images the client has successfully PUT to S3 (presign
// no longer creates rows). Idempotent + ownership-checked server-side; used by
// the onboarding and admin add flows. Returns { added: number }.
export const confirmPropertyImages = createAsyncThunk(
  "/property/:id/images/confirm",
  async ({ id, addedImages }: ConfirmPropertyImagesBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/property/${id}/images`, {
        addedImages,
      });
      return response.data.data as { added: number };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Confirm images failed"
        );
      }
      return rejectWithValue("Confirm images failed");
    }
  }
);

// Post-onboarding photos — homeowner edit request flow
export const editRequestImagesPresignedUrl = createAsyncThunk(
  "/property/:id/edit-request/images-presigned-url",
  async (body: GetImagesPresignedUrlBody, { rejectWithValue }) => {
    try {
      const { id, images } = body;
      const response = await apiClient.post(
        `/property/${id}/edit-request/images-presigned-url`,
        {
          images,
        }
      );
      return response.data.data as ImagesPresignedUrlResponse;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get edit request presigned URLs failed"
        );
      }
      return rejectWithValue("Get edit request presigned URLs failed");
    }
  }
);

// Post-onboarding photos — admin/super-admin direct sync
export const syncPropertyImages = createAsyncThunk(
  "/property/:id/images/sync",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/property/${id}/images/sync`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Sync images failed"
        );
      }
      return rejectWithValue("Sync images failed");
    }
  }
);

// Post-onboarding photos — admin/super-admin delete
export const deletePropertyImage = createAsyncThunk(
  "/property/:pid/images/:iid",
  async (
    { propertyId, imageId }: { propertyId: string; imageId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.delete(
        `/property/${propertyId}/images/${imageId}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Delete image failed"
        );
      }
      return rejectWithValue("Delete image failed");
    }
  }
);

export const finishPropertyOnboarding = createAsyncThunk(
  "/property/:id/finish-onboarding",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `/property/${id}/finish-onboarding`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Finish property onboarding failed"
        );
      }
      return rejectWithValue("Finish property onboarding failed");
    }
  }
);

export const getAmenities = createAsyncThunk(
  "properties/amenities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("property/get-amenities");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get properties failed"
        );
      }
      return rejectWithValue("Get properties failed");
    }
  }
);

export const searchProperties = createAsyncThunk(
  "properties/search",
  async (body: SearchPropertiesBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("properties/search", {
        params: body,
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Search Properties failed"
        );
      }
      return rejectWithValue("Search Properties failed");
    }
  }
);

export const updatePropertyUnit = createAsyncThunk(
  "property/unit/update",
  async (
    {
      unitId,
      data,
    }: {
      unitId: string;
      data: {
        name: string;
        description: string;
        size: number;
        // TODO: Update bedrooms instead of number of bedrooms, etc.
        numberOfBedrooms: number;
        numberOfFullBathrooms: number;
        numberOfHalfBathrooms: number;
        numberOfParkingSpots: number;

        sleepTotal: number;
        wifiName: string | null;
        wifiPassword: string | null;
        petFriendly: boolean;
        streamlineAmenities: string[];
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.put(`property/unit/${unitId}`, data);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Update property unit failed"
        );
      }
      return rejectWithValue("Update property unit failed");
    }
  }
);

export const getShareLink = createAsyncThunk(
  "property/:id/share-link",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`property/${id}/share-link`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Get properties failed"));
    }
  }
);

export const getMapPins = createAsyncThunk(
  "properties/map-pins",
  async ({ numberOfBedrooms, ...data }: GetMapPinsBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("properties/map-pins", {
        params: {
          ...data,
          numberOfBedrooms: numberOfBedrooms !== DEFAULT_BEDROOM_COUNT ? numberOfBedrooms : undefined,
        },
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get map pins for properties failed"
        );
      }
      return rejectWithValue("Get map pins for properties failed");
    }
  }
);

export const updateProperty = createAsyncThunk(
  "property/update",
  async ({ id, ...data }: UpdatePropertyBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`property/${id}`, data);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Update property failed"
        );
      }
      return rejectWithValue("Update property failed");
    }
  }
);

export const deleteProperty = createAsyncThunk<void, string, { rejectValue: string }>(
  "property/delete",
  async (propertyId, { rejectWithValue }) => {
    try {
      await apiClient.delete(`property/${propertyId}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Failed to delete property"
        );
      }
      return rejectWithValue("Failed to delete property");
    }
  }
);

/**
 * True when the signed-in user is any kind of admin, i.e. someone who creates
 * properties on behalf of a homeowner. Such users never sign documents, so
 * their step data must never include SIGN_DOCUMENTS. Falls back to false while
 * the user type is still loading, so a homeowner's flow is never stripped.
 */
const isAdminActingUser = (getState: () => unknown): boolean => {
  const userType = (getState() as RootState).Auth?.userType;
  return (
    userType === USER_TYPES.ADMIN ||
    userType === USER_TYPES.SUPER_ADMIN ||
    userType === USER_TYPES.MAINSAIL_ADMIN
  );
};

/**
 * Fetches the current step information for a property based on the user's role.
 * The backend determines which steps are assigned to the user and returns navigation info.
 */
export const getCurrentStep = createAsyncThunk<
  PropertyCurrentStepResponse,
  string,
  { rejectValue: string }
>("property/current-step", async (propertyId, { rejectWithValue, getState }) => {
  try {
    const response = await apiClient.get(`/property/${propertyId}/current-step`);
    return stripSignDocumentsForAdmin(
      response.data.data,
      isAdminActingUser(getState)
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(
        error.response?.data?.error?.message || "Failed to get current step"
      );
    }
    return rejectWithValue("Failed to get current step");
  }
});

/**
 * Marks one or more features as complete for a property.
 * Call this after saving feature data to advance the property through the onboarding flow.
 */
export const completeFeatures = createAsyncThunk<
  PropertyCurrentStepResponse,
  CompleteFeaturesBody,
  { rejectValue: string }
>("property/features-complete", async ({ id, features }, { rejectWithValue, getState }) => {
  try {
    const response = await apiClient.post(`/property/${id}/features-complete`, {
      features,
    });
    return stripSignDocumentsForAdmin(
      response.data.data.stepInfo,
      isAdminActingUser(getState)
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(
        error.response?.data?.error?.message || "Failed to complete features"
      );
    }
    return rejectWithValue("Failed to complete features");
  }
});
